import axios from 'axios'
import axiosRetry from 'axios-retry'
import { showToast } from './toast'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 45000,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  } else if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json'
  }
  return config
})

axiosRetry(api, {
  retries: 2,
  retryDelay: (retryCount) => retryCount * 2000,
  retryCondition: (error) =>
    (error.code === 'ECONNABORTED' || error.message.includes('timeout')) &&
    error.response?.status !== 401,
})

let isRedirecting = false
let isRefreshing = false
let refreshQueue: Array<(retry: boolean) => void> = []

function flushQueue(retry: boolean) {
  refreshQueue.forEach(cb => cb(retry))
  refreshQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) return Promise.reject(error)
    if (isRedirecting) return Promise.reject(error)

    const status = error.response?.status
    const config = error.config

    const isLoginUrl = config?.url?.includes('/api/admin/auth/login')
    const isRefreshUrl = config?.url?.includes('/api/admin/auth/refresh')

    if (status === 401 && !config?._retry && !isLoginUrl && !isRefreshUrl) {
      config._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((success) => {
            if (success) resolve(api(config))
            else reject(error)
          })
        })
      }

      isRefreshing = true
      try {
        await api.post('/api/admin/auth/refresh')
        isRefreshing = false
        flushQueue(true)
        return api(config)
      } catch {
        isRefreshing = false
        flushQueue(false)
        isRedirecting = true
        if (!window.location.pathname.startsWith('/login')) {
          const [{ useAuthStore }, { default: router }] = await Promise.all([
            import('@/shared/stores/auth'),
            import('@/app/router'),
          ])
          useAuthStore().clearAuth()
          router.replace({ name: 'login' })
        }
        return Promise.reject(error)
      }
    }

    if (status === 403) {
      showToast('error', 'Acces refuse', 'Permission insuffisante')
    } else if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after']
      const seconds = retryAfter ? parseInt(retryAfter, 10) : 30
      showToast('warn', 'Trop de requetes', `Reessayez dans ${seconds} secondes`)
    } else if (status && status >= 500) {
      showToast('error', 'Erreur serveur', 'Une erreur est survenue, veuillez reessayer')
    } else if (!error.response) {
      showToast('error', 'Connexion perdue', 'Verifiez votre connexion internet')
    }

    return Promise.reject(error)
  },
)

export default api
